import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import type { EnvelopeRecipient, RecipientStatus } from '@workpay/ds-shared/types/envelope';
import styles from './RecipientStatusList.module.scss';

type StatusVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const STATUS_VARIANT: Record<RecipientStatus, StatusVariant> = {
  PENDING: 'neutral',
  SENT: 'info',
  DELIVERED: 'info',
  VIEWED: 'info',
  SIGNED: 'success',
  DECLINED: 'danger',
  AWAITING_UPLOAD: 'warning',
  SCAN_UPLOADED: 'info',
};

interface Props {
  recipients: EnvelopeRecipient[];
  requestId: string;
  requestStatus: string;
}

export function RecipientStatusList({ recipients, requestId, requestStatus }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasResendPermission = useAuthStore((s) => s.hasPermission('ENVELOPES_RESEND'));

  const resendMutation = useMutation({
    mutationFn: (recipientId: string) => dsEnvelopeService.resend(requestId, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsEnvelope', requestId] });
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_RECIPIENT_RESENT', 'Email sent to recipient'),
      });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const canResend = (r: EnvelopeRecipient) => {
    if (!hasResendPermission) return false;
    if (!['SENT', 'SIGNING_IN_PROGRESS'].includes(requestStatus)) return false;
    if (['SENT', 'DELIVERED', 'VIEWED'].includes(r.status)) return true;
    if (r.status === 'PENDING') {
      const activeOrders = recipients
        .filter((x) => !['SIGNED', 'DECLINED'].includes(x.status))
        .map((x) => x.signingOrder ?? 1);
      const lowestActive = Math.min(...activeOrders);
      return (r.signingOrder ?? 1) <= lowestActive;
    }
    return false;
  };

  if (recipients.length === 0) {
    return <p className={styles.empty}>{t('K_DS_NO_RECIPIENTS', 'No recipients')}</p>;
  }

  return (
    <div className={styles.list}>
      {recipients.map((r) => {
        const displayStatus = r.role === 'CC' && r.status === 'SIGNED' ? 'ACKNOWLEDGED' : r.status;
        return (
          <div key={r.id ?? r.email} className={styles.item}>
            <div className={styles.avatar} aria-hidden="true">
              {r.name.charAt(0).toUpperCase() || '?'}
            </div>
            <div className={styles.info}>
              <span className={styles.name}>{r.name}</span>
              <span className={styles.email}>{r.email}</span>
            </div>
            <div className={styles.meta}>
              {canResend(r) && r.id && (
                <Button
                  variant="outline"
                  size="sm"
                  loading={resendMutation.isPending && resendMutation.variables === r.id}
                  disabled={resendMutation.isPending}
                  onClick={() => resendMutation.mutate(r.id!)}
                >
                  {r.status === 'PENDING'
                    ? t('K_DS_BUTTON_SEND_EMAIL', 'Send Email')
                    : t('K_DS_BUTTON_RESEND', 'Resend')}
                </Button>
              )}
              <span
                className={`${styles.badge} ${styles[`badge_${STATUS_VARIANT[r.status]}`] ?? ''}`}
              >
                {displayStatus}
              </span>
              <span className={styles.method}>{r.signingMethod}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
